import * as React from 'react';

import type { InlineEmojiData } from '@gitbook/doc-core';
import type { Inline } from '@gitbook/slate';
import type { InlineProps } from '@gitbook/slate-react';
import { Emoji, EmojiPicker, Paper, usePopoverClose } from '@gitbook/spine-react';
import { useEventCallback } from '@gitbook/util-react-hooks';

import { getBlockFontSize, VoidInlineWithPopover } from '../common';

/**
 * Renderer for an emoji inline node.
 */
export function InlineEmoji(props: InlineProps): React.ReactElement {
    const { node, parent } = props;

    return (
        <VoidInlineWithPopover
            {...props}
            renderSpan={() => <InlineEmojiSpan node={node} parent={parent} />}
            popover={<InlineEmojiPopover {...props} />}
        />
    );
}

function InlineEmojiPopover(props: InlineProps): React.ReactElement {
    const { node, editor } = props;

    const [query, setQuery] = React.useState('');
    const closePopover = usePopoverClose();

    const onPick = useEventCallback((value: string) => {
        editor.change((change) => {
            change.setNodeByKey(node.key, {
                data: {
                    code: value,
                },
            });
        });
        closePopover();
    });

    return (
        <Paper className="flex flex-col">
            <EmojiPicker onPick={(emoji) => onPick(emoji)} query={query} onChangeQuery={setQuery} />
        </Paper>
    );
}

function InlineEmojiSpan(props: {
    node: Inline;
    parent: InlineProps['parent'];
}): React.ReactElement {
    const { node, parent } = props;

    const { code } = node.data.toFrozenObject() as InlineEmojiData;
    const parentFontSize = getBlockFontSize(parent.object === 'block' ? parent : null);

    return <Emoji code={code} size={parentFontSize} />;
}
